import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { InteractionStatus } from '@azure/msal-browser';
import { Table, Column } from '../lib/table';
import { Paginator } from '../lib/paginator';
import { Button } from '../lib/button';
import { FormField } from '../lib/forms';
import styles from './MasterDataPage.module.scss';
import { getDomains, getFields, searchData, updateCell, Domain, Field, SearchResult, CellValue, getMetadataSchema } from '../api/service';
import { apiServiceReady } from '../service/ApiService';
import { EditableCell, EditableCellType } from '../components/EditableCell';
import { ExportDialog } from '../components/ExportDialog';
import { ImportDialog } from '../components/ImportDialog';
import { ImportFailedDialog } from '../components/ImportFailedDialog';
import { ImportErrorDetail, ImportResponse } from '../service/importExportService';
import { Dialog } from '../lib/dialog';
import { getErrorMessage } from '../utils/errorUtils';
import { LOCATION_IMPORT_DOMAIN_ID } from '../utils/locationImportUtils';
import { useDataSelection } from '../context/DataSelectionContext';
import { getPalette } from '../utils/paletteUtils';



type Row = Record<string, CellValue>;

const MAX_VISIBLE_FILTERS = 4;

type PendingSave = {
  rowId: string | number;
  column: string;
  newValue: CellValue;
  oldValue: CellValue;
  resolve: () => void;
  reject: (err: Error) => void;
};



const MasterDataPage: React.FC = () => {
  const navigate = useNavigate();
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { selection: savedSelection } = useDataSelection();

  const userEmail    = accounts[0]?.username ?? accounts[0]?.name ?? '';
  const userInitials = userEmail ? userEmail.split('@')[0].slice(0, 2).toUpperCase() : '?';
  const handleLogout = () => instance.logoutRedirect();

  // ── Domain & field selection state ──────────────────────────────────────
  const [domains, setDomains]                   = useState<Domain[]>([]);
  const [selectedDomainId, setSelectedDomainId] = useState<string | undefined>(undefined);
  const [availableFields, setAvailableFields]   = useState<Field[]>([]);
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([]);
  const [rootIndexMap, setRootIndexMap]         = useState<Map<string, number>>(new Map());

  // ── Filters ────────────────────────────────────────────────────────────
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  // ── Data ───────────────────────────────────────────────────────────────
  const [result, setResult] = useState<SearchResult | null>(null);
  const [error, setError]   = useState<string | null>(null);
  const [page, setPage]     = useState(1);
  const [searchRequestId, setSearchRequestId] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const pageSize = 20;

  // ── Import / Export ────────────────────────────────────────────────────
  const [exportOpen, setExportOpen]   = useState(false);
  const [importOpen, setImportOpen]   = useState(false);
  const [failedOpen, setFailedOpen]   = useState(false);
  const [importErrors, setImportErrors] = useState<ImportErrorDetail[]>([]);
  const [importSuccessOpen, setImportSuccessOpen] = useState(false);
  const [importSuccessResult, setImportSuccessResult] = useState<ImportResponse | null>(null);

  // ── Inline-edit ────────────────────────────────────────────────────────
  const [editingCell, setEditingCell] = useState<{ rowId: string | number; column: string } | null>(null);
  const [pendingSave, setPendingSave] = useState<PendingSave | null>(null);

  // ── Load domains and schema ────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || inProgress !== InteractionStatus.None) return;
    void apiServiceReady.then(() => {
      getDomains()
        .then(setDomains)
        .catch((err: unknown) => setError(`Failed to load domains: ${getErrorMessage(err)}`));
      
      getMetadataSchema()
        .then((s) => {
          const idxMap = new Map<string, number>();
          s.root.forEach((n, i) => idxMap.set(n.id, i));
          setRootIndexMap(idxMap);
        })
        .catch((err: unknown) => console.error('Failed to load schema:', err));
    });
  }, [isAuthenticated, inProgress]);

  // ── Apply saved selection ───────────────────────────────────────────────
  useEffect(() => {
    if (!savedSelection?.path || savedSelection.path.length === 0) {
      setSelectedDomainId(undefined);
      setSelectedFieldIds([]);
      setResult(null);
      return;
    }
    const entityId = savedSelection.path[savedSelection.path.length - 1];
    setSelectedDomainId(entityId);
    setSelectedFieldIds(savedSelection.selectedColumns);
    setAvailableFields([]);
    setFilterValues({});
    setResult(null);
    setError(null);
    setPage(1);
    getFields(entityId)
      .then((fields) => setAvailableFields(fields))
      .catch((err) => setError(`Failed to load fields: ${err.message}`));
  }, [savedSelection]);

  const activeDomain = useMemo(() => {
    if (!savedSelection?.path?.length) {
      return domains.find((d) => d.id === selectedDomainId);
    }

    const rootId = savedSelection.path[0];
    return domains.find((d) => d.id === selectedDomainId) ?? domains.find((d) => d.id === rootId);
  }, [domains, selectedDomainId, savedSelection]);
  const primaryKey   = activeDomain?.primaryKey ?? null;

  // ── Calculate root index and header palette ─────────────────────────────
  const currentRootIdx = useMemo(() => {
    if (!savedSelection?.path?.length) return 0;
    const domainId = savedSelection.path[0];
    return rootIndexMap.get(domainId) ?? 0;
  }, [savedSelection, rootIndexMap]);

  const headerPalette = useMemo(() => {
    return getPalette(currentRootIdx, 0);
  }, [currentRootIdx]);

  const tableHeaderStyle = useMemo(
    () => ({
      '--master-table-header-bg': headerPalette.ring,
    }) as React.CSSProperties,
    [headerPalette],
  );

  // Fall back to common identifier naming when domain metadata does not expose a primary key.
  const effectivePrimaryKey = useMemo(() => {
    if (primaryKey) return primaryKey;

    const columnKeys = result?.columns.map((c) => c.key) ?? [];
    const firstRow = result?.data?.[0] ?? null;
    const rowKeys = firstRow ? Object.keys(firstRow) : [];
    const candidates = ['id', 'Id', 'ID'];

    for (const key of candidates) {
      if (columnKeys.includes(key) || rowKeys.includes(key)) {
        return key;
      }
    }

    // Check columnKeys too — backend may report the column even if the first row is null.
    const allIdLikeKeys = [
      ...new Set([
        ...columnKeys.filter((k) => k.toLowerCase() === 'id' || k.toLowerCase().endsWith('id')),
        ...rowKeys.filter((k) => k.toLowerCase() === 'id' || k.toLowerCase().endsWith('id')),
      ]),
    ];
    const exactId = allIdLikeKeys.find((k) => k.toLowerCase() === 'id');
    if (exactId) return exactId;
    // Prefer the shortest id-like key (most likely to be the primary key).
    if (allIdLikeKeys.length > 0) {
      return allIdLikeKeys.sort((a, b) => a.length - b.length)[0];
    }
    return null;
  }, [primaryKey, result]);

  const searchFieldIds = useMemo(() => {
    // Ensure the primary key is always fetched so effectivePrimaryKey can work.
    if (primaryKey) {
      if (selectedFieldIds.includes(primaryKey)) return selectedFieldIds;
      return [primaryKey, ...selectedFieldIds];
    }
    // No explicit primaryKey — try to find a candidate from the known available fields
    // so we include it in the search without guessing field names the backend doesn't know.
    const pkCandidate =
      availableFields.find((f) => ['id', 'Id', 'ID'].includes(f.id)) ??
      availableFields.find((f) => f.id.toLowerCase().endsWith('id'));
    if (!pkCandidate || selectedFieldIds.includes(pkCandidate.id)) {
      return selectedFieldIds;
    }
    return [pkCandidate.id, ...selectedFieldIds];
  }, [primaryKey, selectedFieldIds, availableFields]);

  const fieldTypeMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const f of availableFields) m[f.id] = f.type;
    return m;
  }, [availableFields]);

  const handleSaveCell = useCallback(async (rowId: string | number, column: string, newValue: CellValue): Promise<void> => {
    if (!selectedDomainId) throw new Error('No domain selected');
    try {
      const updated = await updateCell(selectedDomainId, rowId, column, newValue);
      setResult((prev) => {
        if (!prev || !effectivePrimaryKey) return prev;
        const nextData = prev.data.map((r) =>
          String(r[effectivePrimaryKey]) === String(rowId) ? { ...r, [column]: updated.value } : r,
        );
        return { ...prev, data: nextData };
      });
      setEditingCell(null);
      setError(null);
    } catch (err: unknown) {
      const msg = `Update failed: ${getErrorMessage(err)}`;
      setError(msg);
      setEditingCell(null);
      throw new Error(msg);
    }
  }, [selectedDomainId, effectivePrimaryKey]);

  const tableColumns: Column<Row>[] = useMemo(() => {
    const baseCols = result?.columns
      ? result.columns
          .filter((col) => col.key !== effectivePrimaryKey || selectedFieldIds.includes(col.key))
          .map((col) => ({ key: col.key, label: col.label }))
      : selectedFieldIds.map((id) => {
          const field = availableFields.find((f) => f.id === id);
          return { key: id, label: field?.label ?? id };
        });

    return baseCols.map(({ key, label }) => {
      const fieldType    = (fieldTypeMap[key] ?? 'string') as EditableCellType;
      return {
        key,
        title: label,
        render: (item: Row) => {
          if (!effectivePrimaryKey) {
            return (
              <EditableCell
                value={item[key]}
                type={fieldType}
                editable={false}
                isEditing={false}
                onStartEdit={() => undefined}
                onCancel={() => undefined}
                onCommit={async () => undefined}
              />
            );
          }

          const rowIdValue = item[effectivePrimaryKey] as string | number | null | undefined;
          const hasRowId = rowIdValue !== undefined && rowIdValue !== null && String(rowIdValue).trim() !== '';
          if (!hasRowId) {
            return (
              <EditableCell
                value={item[key]}
                type={fieldType}
                editable={false}
                isEditing={false}
                onStartEdit={() => undefined}
                onCancel={() => undefined}
                onCommit={async () => undefined}
              />
            );
          }

          const rowId: string | number = rowIdValue as string | number;
          const cellEditable = activeDomain?.editable ?? true;
          const isEditing = !!editingCell && String(editingCell.rowId) === String(rowId) && editingCell.column === key;
          return (
            <EditableCell
              value={item[key]}
              type={fieldType}
              editable={cellEditable}
              isEditing={isEditing}
              onStartEdit={() => { if (activeDomain?.editable) setEditingCell({ rowId, column: key }); }}
              onCancel={() => setEditingCell(null)}
              onCommit={(newValue) =>
                new Promise<void>((resolve, reject) => {
                  setPendingSave({ rowId, column: key, newValue, oldValue: item[key], resolve, reject });
                })
              }
            />
          );
        },
      };
    });
  }, [result, selectedFieldIds, availableFields, fieldTypeMap, effectivePrimaryKey, editingCell]);

  const updateFilter = useCallback((fieldId: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  const handleSearch = useCallback(async (targetPage = 1) => {
    if (!selectedDomainId) return;
    setError(null);
    setIsSearching(true);
    setResult(null);
    setSearchRequestId((prev) => prev + 1);
    try {
      const activeFilters: Record<string, string> = {};
      for (const [k, v] of Object.entries(filterValues)) {
        if (v.trim()) activeFilters[k] = v.trim();
      }
      const data = await searchData(selectedDomainId, searchFieldIds, activeFilters, targetPage, pageSize);
      setResult(data);
      setPage(targetPage);
    } catch (err: unknown) {
      setError(`Search failed: ${getErrorMessage(err)}`);
    } finally {
      setIsSearching(false);
    }
  }, [selectedDomainId, searchFieldIds, filterValues, pageSize]);

  const handlePageChange = useCallback((newPage: number) => handleSearch(newPage), [handleSearch]);

  // ── Auto-search when domain + fields ready ─────────────────────────────
  // searchFieldIds depends on availableFields (loaded async) — when availableFields
  // arrives and a pk candidate is detected, searchFieldIds changes and triggers a
  // fresh search that includes the primary key so editing works.
  // eslint-disable-next-line react-hooks/exhaustive-deps
 



 useEffect(() => {
    if (!selectedDomainId || searchFieldIds.length === 0 || availableFields.length === 0) return;
    void handleSearch(1);
}, [selectedDomainId, searchFieldIds, availableFields, handleSearch]);



  const filterableFields = useMemo(
    () => availableFields.filter((f) => selectedFieldIds.includes(f.id) && f.type === 'string'),
    [availableFields, selectedFieldIds],
  );

  const handleImportFailure = (errors: ImportErrorDetail[]) => { setImportErrors(errors); setFailedOpen(true); };
  const handleImportSuccess = (result: ImportResponse) => {
    if (result.domain_id === LOCATION_IMPORT_DOMAIN_ID) {
      setImportSuccessResult(result);
      setImportSuccessOpen(true);
      return;
    }
    handleSearch(1);
  };

  const exportFilters = useMemo(() => {
    const activeFilters: Record<string, string> = {};
    for (const [k, v] of Object.entries(filterValues)) {
      if (v && v.trim()) activeFilters[k] = v.trim();
    }
    return { domain_id: selectedDomainId, filters: activeFilters, fields: selectedFieldIds.length > 0 ? selectedFieldIds : undefined };
  }, [selectedDomainId, filterValues, selectedFieldIds]);

  const tableKey = useMemo(() => {
    return `${selectedDomainId ?? 'none'}::${searchRequestId}`;
}, [selectedDomainId, searchRequestId]);
  return (
    <div className={styles.page}>

      {/* ════════ TopBar ════════ */}
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <div className={styles.topBarLogoMark}>M</div>
          <span className={styles.topBarTitle}>Master Data</span>
        </div>
        <div className={styles.topBarRight}>
          {userEmail && (
            <div className={styles.topBarUserPill}>
              <div className={styles.topBarAvatar}>{userInitials}</div>
              <span className={styles.topBarEmail}>{userEmail}</span>
            </div>
          )}
          <button type="button" className={styles.topBarLogoutBtn} onClick={handleLogout}>
            Sign out
          </button>
        </div>
      </div>

      {/* ════════ No Selection State ════════ */}
      {!savedSelection?.path || savedSelection.path.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center', color: '#666' }}>
          <div style={{ fontSize: '1.1rem', marginBottom: '12px', fontWeight: 500 }}>No data selection yet</div>
          <div style={{ fontSize: '0.9rem', marginBottom: '24px', color: '#888' }}>
            Please start by selecting data from the Data Selection page
          </div>
          <Button variant="primary" onClick={() => navigate('/data-selection')}>Go to Data Selection</Button>
        </div>
      ) : (
        <>
          {/* ════════ Current Selection Summary ════════ */}
          {savedSelection?.path && savedSelection.path.length > 0 && (
            <div className={styles.filterPanel} style={{ borderLeft: '4px solid #0B419D' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0B419D', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Current Selection
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#333', lineHeight: '1.5' }}>
                    <strong>{savedSelection.path.join(' → ')}</strong>
                    <br />
                    <span style={{ color: '#666', fontSize: '0.85rem' }}>
                      {savedSelection.selectedColumns.length} column{savedSelection.selectedColumns.length !== 1 ? 's' : ''} selected
                    </span>
                  </div>
                </div>
                <Button onClick={() => navigate('/data-selection', { state: { transition: 'master-to-data' } })} variant="primary" size="small" style={{ whiteSpace: 'nowrap' }}>
                  ✎ Edit Selection
                </Button>
              </div>
            </div>
          )}

          {/* ════════ Filter Panel ════════ */}
          {selectedDomainId && selectedFieldIds.length > 0 && (
            <div className={styles.filterPanel}>
              {filterableFields.length > 0 && (
                <div className={styles.filterRow}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#666', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Search & Filter
                    </div>
                  </div>
                  {filterableFields.slice(0, MAX_VISIBLE_FILTERS).map((field) => (
                    <FormField key={field.id} label={field.label} className={styles.filterField} showNecessity={false}>
                      <input
                        type="text"
                        className="input is-small"
                        placeholder={`Filter ${field.label}...`}
                        value={filterValues[field.id] || ''}
                        onChange={(e) => updateFilter(field.id, e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void handleSearch(1); }} //enter button to trigger search
                      />
                    </FormField>
                  ))}
                  {filterableFields.length > MAX_VISIBLE_FILTERS && (
                    <span style={{ fontSize: '0.75rem', color: '#888', alignSelf: 'flex-end', paddingBottom: 8 }}>
                      +{filterableFields.length - MAX_VISIBLE_FILTERS} more filter{filterableFields.length - MAX_VISIBLE_FILTERS !== 1 ? 's' : ''}
                    </span>
                  )}
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => void handleSearch(1)}
                    style={{ alignSelf: 'flex-end', marginBottom: 8 }}//enter button 
                  >
                    Search
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ════════ Error ════════ */}
          {selectedDomainId && error && (
            <div role="alert" style={{ color: '#dc3545', padding: '12px 16px', marginBottom: '16px', backgroundColor: '#fff5f5', borderLeft: '4px solid #dc3545' }}>
              ⚠ {error}
            </div>
          )}

          {/* ════════ Table ════════ */}
          {selectedDomainId && selectedFieldIds.length > 0 && (
            <div className={styles.tableWrapper} style={{ ...tableHeaderStyle, margin: '24px 32px', flex: 1 }}>
              {result && (
                <div style={{ padding: '4px 8px', fontSize: '0.85rem', color: '#666', marginBottom: 4 }}>
                  Showing {result.data.length} of {result.totalCount} rows
                </div>
              )}
              {isSearching && !result && (
                <div style={{ padding: '24px 8px', color: '#666', textAlign: 'center' }}>
                  Searching...
                </div>
              )}
              <Table
                key={tableKey}
                data={result?.data ?? []}
                columns={tableColumns}
                rowKey={(item, index) => (effectivePrimaryKey ? String(item[effectivePrimaryKey]) : String(index))}
              />
              {result && result.totalCount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px', padding: '0 8px 16px' }}>
                  <Paginator current={page} total={result.totalCount} pageSize={pageSize} onChange={handlePageChange} />
                </div>
              )}
            </div>
          )}

          {/* ════════ Action Buttons ════════ */}
{selectedDomainId && selectedFieldIds.length > 0 && (
    <div className={styles.actionsBar}>
        <div className={styles.actionsLeft}>
            <Button onClick={() => setExportOpen(true)}>Export</Button>
            <div title={!activeDomain?.editable ? "This domain is read-only and cannot be imported" : ""}>
                <Button 
                    onClick={() => activeDomain?.editable && setImportOpen(true)}
                    style={{ 
                        opacity: activeDomain?.editable ? 1 : 0.4,
                        cursor: activeDomain?.editable ? 'pointer' : 'not-allowed',
                        pointerEvents: 'all'
                    }}
                >
                    Import
                </Button>
            </div>
        </div>
    </div>
)}

          {/* ════════ Dialogs ════════ */}
          <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)} filters={exportFilters} />
          <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} domainId={selectedDomainId} fields={availableFields} onSuccess={handleImportSuccess} onFailure={handleImportFailure} />
          <ImportFailedDialog open={failedOpen} onClose={() => setFailedOpen(false)} errors={importErrors} />

          <Dialog
            open={importSuccessOpen}
            onClose={() => { setImportSuccessOpen(false); setImportSuccessResult(null); }}
            title="Location validation complete"
            footer={
              <Button variant="primary" onClick={() => { setImportSuccessOpen(false); setImportSuccessResult(null); }}>
                Close
              </Button>
            }
          >
            {importSuccessResult && (
              <>
                <p style={{ marginTop: 0 }}>
                  {importSuccessResult.rows_accepted} of {importSuccessResult.rows_processed} row
                  {importSuccessResult.rows_processed !== 1 ? 's' : ''} passed validation.
                </p>
                <p className="help" style={{ marginBottom: 0 }}>
                  Rows are not saved to the Sites table yet — this step only checks your HSE file against master data rules.
                </p>
              </>
            )}
          </Dialog>

          {/* ════════ Confirm inline-edit save ════════ */}
          <Dialog
            open={!!pendingSave}
            onClose={() => { pendingSave?.reject(new Error('cancelled')); setEditingCell(null); setPendingSave(null); }}
            title="Confirm change"
            preventCloseOnOverlayClick
            footer={
              <Button variant="primary" onClick={() => {
                if (!pendingSave) return;
                const { rowId, column, newValue, resolve, reject } = pendingSave;
                setPendingSave(null);
                void handleSaveCell(rowId, column, newValue).then(resolve).catch(reject);
              }}>Save</Button>
            }
            footerExtras={
              <Button onClick={() => { pendingSave?.reject(new Error('cancelled')); setEditingCell(null); setPendingSave(null); }}>Cancel</Button>
            }
          >
            <p style={{ margin: 0 }}>Are you sure you want to save this change?</p>
            {pendingSave && (
              <table style={{ marginTop: 12, borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <tbody>
                  <tr>
                    <td style={{ paddingRight: 16, color: '#666' }}>Field</td>
                    <td><strong>{pendingSave.column}</strong></td>
                  </tr>
                  <tr>
                    <td style={{ paddingRight: 16, color: '#666' }}>From</td>
                    <td>{pendingSave.oldValue == null ? <em style={{ color: '#999' }}>empty</em> : String(pendingSave.oldValue)}</td>
                  </tr>
                  <tr>
                    <td style={{ paddingRight: 16, color: '#666' }}>To</td>
                    <td><strong>{pendingSave.newValue == null ? <em style={{ color: '#999' }}>empty</em> : String(pendingSave.newValue)}</strong></td>
                  </tr>
                </tbody>
              </table>
            )}
          </Dialog>
        </>
      )}
    </div>
  );
};

export default MasterDataPage;



